import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Success, Confirmation, Error } from "../../Components/Modal";
import Modal from "react-bootstrap/Modal";
import PageHeader from "../../Components/PageHeader";
import { getLandingContent, upsertLandingContent, resetLandingContent, generateVideoThumbnail } from "../../Services/LandingContent";
import { image_url } from "../../Utils/connection_strings";
import "./LandingCMS.css";

const STORAGE_KEY = "muvrrs_landing_content";

const DEFAULT_CONTENT = {
  hero: {
    badge: "Next-Gen Same-Day Delivery Platform",
    title: "Your Trusted Same-Day Delivery Partner",
    subtitle: "Fast, insured same-day delivery for furniture, appliances, electronics, and bulky items, with real-time tracking from pickup to doorstep.",
    appStoreUrl: "https://apps.apple.com/",
    playStoreUrl: "https://play.google.com/store/apps"
  },
  videoSection: {
    eyebrow: "Watch MUVRRS in Action",
    mainTitle: "Explore Our Video Showcase",
    mainSubtitle: "Discover how MUVRRS makes same-day delivery effortless, transparent, and completely insured from start to finish.",
    videos: [
      {
        id: "vid-1",
        title: "See MUVRRS in Action - Same-Day Delivery",
        description: "Watch how easy it is to book same-day delivery, track your driver live, and get bulky items to your door — all from the app.",
        url: "https://avtshare01.rz.tu-ilmenau.de/avt-vqdb-uhd-1/test_1/segments/bigbuck_bunny_8bit_15000kbps_1080p_60.0fps_hevc.mp4",
        poster: "./images/app_screen.png",
        tag: "Featured Overview"
      },
      {
        id: "vid-2",
        title: "Instant Booking & Live GPS Driver Tracking",
        description: "Experience lightning-fast booking with accurate ETAs, live GPS vehicle tracking, and automated delivery updates.",
        url: "https://avtshare01.rz.tu-ilmenau.de/avt-vqdb-uhd-1/test_1/segments/bigbuck_bunny_8bit_15000kbps_1080p_60.0fps_hevc.mp4",
        poster: "./images/srep_img.png",
        tag: "How It Works"
      },
      {
        id: "vid-3",
        title: "Safe & Insured Handling for Heavy Items",
        description: "Every delivery is protected by full insurance and handled by professional, background-checked drivers you can trust.",
        url: "https://avtshare01.rz.tu-ilmenau.de/avt-vqdb-uhd-1/test_1/segments/bigbuck_bunny_8bit_15000kbps_1080p_60.0fps_hevc.mp4",
        poster: "./images/contact_bg.png",
        tag: "Trust & Safety"
      }
    ]
  },
  howItWorks: {
    eyebrow: "Steps",
    title: "How it works",
    subtitle: "Book a delivery in minutes and track every step from pickup to dropoff.",
    steps: [
      { id: "step-1", title: "Step 1", text: "Enter pickup & delivery address and choose your preferred time." },
      { id: "step-2", title: "Step 2", text: "Get matched with a verified driver who handles heavy lifting." },
      { id: "step-3", title: "Step 3", text: "Track your delivery live on the map until safe arrival." }
    ]
  },
  about: {
    eyebrow: "About MUVRRS",
    title: "About MUVRRS",
    subtitle: "MUVRRS is a modern same-day delivery platform that connects you with trusted drivers for the fast and secure transport of furniture, appliances, electronics, and other heavy items.",
    features: [
      { id: "feat-1", title: "Same-Day Delivery", text: "Book in minutes and deliver your items the same day, with live tracking and clear ETAs." },
      { id: "feat-2", title: "Insured Shipments", text: "Every delivery is covered by insurance so you can ship with confidence." },
      { id: "feat-3", title: "Highly Rated Drivers", text: "Background-checked, experienced drivers equipped for bulky and fragile items." }
    ]
  },
  whyChooseUs: {
    eyebrow: "Why Choose Us",
    title: "Built for modern deliveries — fast, secure, reliable.",
    subtitle: "Everything you need for seamless deliveries: instant booking, real-time tracking, and trusted drivers you can rely on.",
    featured: {
      title: "Real-Time Tracking",
      text: "Monitor your driver live with precise GPS updates and instant status alerts every mile of the way.",
      stats: [
        { value: "99.4%", label: "On-Time Rate" },
        { value: "50K+", label: "Deliveries Done" },
        { value: "4.9★", label: "Customer Rating" }
      ]
    },
    cards: [
      { id: "card-1", title: "Instant Pickup Booking", text: "Schedule a pickup in just a few taps with guaranteed driver availability." },
      { id: "card-2", title: "Secure Deliveries", text: "Comprehensive insurance coverage and photo verification at pickup and dropoff." },
      { id: "card-3", title: "Fast Delivery Options", text: "Choose from same-day, next-day, or standard delivery services based on your needs." },
      { id: "card-4", title: "Delivery Notifications", text: "Receive timely alerts for pickup confirmation, transit updates, and successful delivery." }
    ]
  },
  screenshots: {
    title: "App Screenshots",
    subtitle: "See how the app looks across key screens — clean, fast, and built for everyday use.",
    items: [
      "./images/as-1.png",
      "./images/as-2.png",
      "./images/as-3.png",
      "./images/as-4.png",
      "./images/as-2.png"
    ]
  },
  testimonials: {
    eyebrow: "TESTIMONIALS",
    title: "We want to show Our clients Reviews",
    items: [
      { id: "test-1", name: "Mireld Rock", quote: "Booking same-day delivery was effortless. Real-time tracking kept me updated from pickup to doorstep every step of the way.", image: "./images/testimonials-client.png" },
      { id: "test-2", name: "Sarah Mitchell", quote: "Our furniture arrived safely and on time. The drivers were professional and the whole experience felt premium.", image: "./images/testimonials-client.png" }
    ]
  },
  ctaDownload: {
    title: "Get the MUVRRS App. Anytime, Anywhere.",
    subtitle: "A faster, offline-ready experience tailored to your device and designed for fast deliveries.",
    appStoreUrl: "https://apps.apple.com/",
    playStoreUrl: "https://play.google.com/store/apps"
  },
  faq: {
    eyebrow: "Frequently Asked Questions",
    title: "Frequently Asked Questions",
    subtitle: "Got questions? We have answers.",
    items: [
      { id: "faq-1", question: "What items can I ship with MUVRRS?", answer: "We handle furniture, home appliances, electronics, retail purchases, and bulky or fragile items that standard couriers won't take." },
      { id: "faq-2", question: "How long does delivery take?", answer: "Most deliveries are completed the same day. Exact timing depends on distance, item type, and driver availability — you’ll see an ETA in the app." },
      { id: "faq-3", question: "Is my delivery insured?", answer: "Yes. Every MUVRRS delivery is covered by insurance so your items are protected from pickup to drop-off." },
      { id: "faq-4", question: "How do I track my delivery?", answer: "Open the MUVRRS app and go to your active order to see live tracking, status updates, and driver details in real time." }
    ]
  },
  contact: {
    eyebrow: "Contact",
    title: "Get in Touch",
    cardTitle: "We're here to help",
    cardText: "Reach out to our team and we'll get back to you within one business day.",
    phone: "+1 (800) MUVRRS-1",
    email: "support@muvrrs.com",
    address: "123 Delivery Ave, New York, NY 10001",
    formTitle: "Send us a message",
    formSubtitle: "Fill out the form and we'll get back to you as soon as possible."
  },
  socials: {
    facebook: "https://www.facebook.com",
    twitter: "https://twitter.com",
    instagram: "https://www.instagram.com",
    linkedin: "https://www.linkedin.com"
  }
};

// Prefer provided arrays (incl. empty) so deleted videos/items stay deleted
const pickArray = (incoming, fallback) =>
  Array.isArray(incoming) ? incoming : fallback;

const deepMergeContent = (parsed) => {
  if (!parsed || typeof parsed !== "object") return DEFAULT_CONTENT;
  return {
    ...DEFAULT_CONTENT,
    ...parsed,
    hero: { ...DEFAULT_CONTENT.hero, ...(parsed.hero || {}) },
    videoSection: {
      ...DEFAULT_CONTENT.videoSection,
      ...(parsed.videoSection || {}),
      videos: pickArray(parsed.videoSection?.videos, DEFAULT_CONTENT.videoSection.videos)
    },
    howItWorks: {
      ...DEFAULT_CONTENT.howItWorks,
      ...(parsed.howItWorks || {}),
      steps: pickArray(parsed.howItWorks?.steps, DEFAULT_CONTENT.howItWorks.steps)
    },
    about: {
      ...DEFAULT_CONTENT.about,
      ...(parsed.about || {}),
      features: pickArray(parsed.about?.features, DEFAULT_CONTENT.about.features)
    },
    whyChooseUs: {
      ...DEFAULT_CONTENT.whyChooseUs,
      ...(parsed.whyChooseUs || {}),
      cards: pickArray(parsed.whyChooseUs?.cards, DEFAULT_CONTENT.whyChooseUs.cards),
      featured: {
        ...DEFAULT_CONTENT.whyChooseUs.featured,
        ...(parsed.whyChooseUs?.featured || {}),
        stats: pickArray(
          parsed.whyChooseUs?.featured?.stats,
          DEFAULT_CONTENT.whyChooseUs.featured.stats
        )
      }
    },
    screenshots: {
      ...DEFAULT_CONTENT.screenshots,
      ...(parsed.screenshots || {}),
      items: pickArray(
        parsed.screenshots?.items ?? parsed.screenshots?.images,
        DEFAULT_CONTENT.screenshots.items
      )
    },
    testimonials: {
      ...DEFAULT_CONTENT.testimonials,
      ...(parsed.testimonials || {}),
      items: pickArray(parsed.testimonials?.items, DEFAULT_CONTENT.testimonials.items)
    },
    ctaDownload: { ...DEFAULT_CONTENT.ctaDownload, ...(parsed.ctaDownload || {}) },
    faq: {
      ...DEFAULT_CONTENT.faq,
      ...(parsed.faq || {}),
      items: pickArray(parsed.faq?.items, DEFAULT_CONTENT.faq.items)
    },
    contact: { ...DEFAULT_CONTENT.contact, ...(parsed.contact || {}) },
    socials: { ...DEFAULT_CONTENT.socials, ...(parsed.socials || {}) }
  };
};

/** Resolve poster/image paths used by the onboarding landing assets */
const resolveLandingMediaUrl = (src) => {
  if (!src || typeof src !== "string") return "";
  const trimmed = src.trim();
  if (!trimmed) return "";
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  const base = (image_url || "").replace(/\/$/, "");
  if (trimmed.startsWith("/images/")) return `${base}${trimmed}`;
  if (trimmed.startsWith("/onboarding-app/")) return `${process.env.PUBLIC_URL || ""}${trimmed}`;
  if (trimmed.startsWith("./")) {
    return `${process.env.PUBLIC_URL || ""}/onboarding-app/${trimmed.slice(2)}`;
  }
  if (trimmed.startsWith("images/")) {
    return `${process.env.PUBLIC_URL || ""}/onboarding-app/${trimmed}`;
  }
  return trimmed;
};

/** Capture a still frame from an MP4 URL for use as poster (best-effort; CORS may block) */
const captureVideoPoster = (videoUrl) =>
  new Promise((resolve) => {
    if (!videoUrl) {
      resolve("");
      return;
    }
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.crossOrigin = "anonymous";
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      video.removeAttribute("src");
      video.load();
      resolve(value || "");
    };

    const timer = setTimeout(() => finish(""), 5000);

    video.addEventListener("error", () => {
      clearTimeout(timer);
      finish("");
    });

    video.addEventListener("loadeddata", () => {
      try {
        video.currentTime = Math.min(1, (video.duration || 2) * 0.1);
      } catch (_) {
        clearTimeout(timer);
        finish("");
      }
    });

    video.addEventListener("seeked", () => {
      try {
        const canvas = document.createElement("canvas");
        const w = video.videoWidth || 640;
        const h = video.videoHeight || 360;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          clearTimeout(timer);
          finish("");
          return;
        }
        ctx.drawImage(video, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.72);
        clearTimeout(timer);
        finish(dataUrl);
      } catch (_) {
        clearTimeout(timer);
        finish("");
      }
    });

    video.src = videoUrl;
  });


export default function LandingPageCMS() {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [activeTab, setActiveTab] = useState("hero");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Modals state
  const [videoModal, setVideoModal] = useState({ show: false, index: null, title: "", description: "", url: "", poster: "", tag: "" });
  const [thumbnailGenerating, setThumbnailGenerating] = useState(false);
  const [stepModal, setStepModal] = useState({ show: false, index: null, title: "", text: "" });
  const [faqModal, setFaqModal] = useState({ show: false, index: null, question: "", answer: "" });
  const [testimonialModal, setTestimonialModal] = useState({ show: false, index: null, name: "", quote: "", image: "" });

  const autoGenerateThumbnail = useCallback(async (url) => {
    const cleanUrl = (url || "").trim();
    if (!cleanUrl || !/^https?:\/\//i.test(cleanUrl)) return "";

    setThumbnailGenerating(true);
    try {
      try {
        const res = await generateVideoThumbnail(cleanUrl);
        const posterPath = res?.data?.data?.poster;
        if (posterPath) return posterPath;
      } catch (apiErr) {
        console.warn("Server thumbnail failed, trying browser capture:", apiErr);
      }

      const captured = await captureVideoPoster(cleanUrl);
      return captured || "";
    } finally {
      setThumbnailGenerating(false);
    }
  }, []);

  // When MP4 URL changes in the modal, auto-generate thumbnail
  useEffect(() => {
    if (!videoModal.show) return undefined;
    const url = (videoModal.url || "").trim();
    if (!url || !/^https?:\/\//i.test(url)) return undefined;
    // Already have a server-generated thumb — keep it until URL changes (URL onChange clears poster)
    if (videoModal.poster && (videoModal.poster.startsWith("/images/landing/") || videoModal.poster.startsWith("data:"))) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      const poster = await autoGenerateThumbnail(url);
      setVideoModal((prev) => {
        if (prev.url.trim() !== url) return prev;
        if (poster) return { ...prev, poster };
        // Thumb API often fails on HEVC/remote URLs — keep a visible default poster
        if (!prev.poster) return { ...prev, poster: "./images/app_screen.png" };
        return prev;
      });
    }, 600);

    return () => clearTimeout(timer);
  }, [videoModal.show, videoModal.url, videoModal.poster, autoGenerateThumbnail]);

  const cacheLocally = useCallback((payload) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.error("Error caching landing content locally:", e);
    }
  }, []);

  const loadFromApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getLandingContent();
      const apiContent = res?.data?.data?.content;
      if (apiContent) {
        const merged = deepMergeContent(apiContent);
        setContent(merged);
        cacheLocally(merged);
        if (res?.data?.data?.updatedAt) {
          setLastSavedAt(res.data.data.updatedAt);
        }
        return;
      }
      throw new Error(res?.data?.message || "Empty landing content response");
    } catch (e) {
      console.error("Landing content API load failed:", e);
      // Fallback: local cache, then defaults
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          setContent(deepMergeContent(JSON.parse(stored)));
          Error("Loaded local draft — API unavailable. Save again once backend is online.");
          return;
        }
      } catch (_) {
        /* ignore */
      }
      setContent(DEFAULT_CONTENT);
      Error(e?.response?.data?.message || "Could not load landing content from server.");
    } finally {
      setIsLoading(false);
    }
  }, [cacheLocally]);

  useEffect(() => {
    loadFromApi();
  }, [loadFromApi]);

  const persistToApi = async (nextContent, successTitle, successMessage) => {
    const merged = deepMergeContent(nextContent);
    setContent(merged);
    setIsSaving(true);
    try {
      const res = await upsertLandingContent(merged);
      const saved = res?.data?.data?.content
        ? deepMergeContent(res.data.data.content)
        : merged;
      setContent(saved);
      cacheLocally(saved);
      if (res?.data?.data?.updatedAt) {
        setLastSavedAt(res.data.data.updatedAt);
      }
      Success(successTitle || "Saved Successfully", successMessage || "Landing page content is live on the server.");
      return true;
    } catch (e) {
      console.error("Landing content save failed:", e);
      Error(e?.response?.data?.message || "Failed to save landing content to server.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleManualSave = () => {
    if (isSaving) return;
    persistToApi(content, "Saved Successfully", "All landing page sections are saved on the backend and ready for the live page.");
  };

  const handleResetDefaults = () => {
    Confirmation("Are you sure you want to reset all landing content to default values on the server?", "Reset Defaults", async () => {
      setIsSaving(true);
      try {
        const res = await resetLandingContent();
        const resetData = res?.data?.data?.content
          ? deepMergeContent(res.data.data.content)
          : DEFAULT_CONTENT;
        setContent(resetData);
        cacheLocally(resetData);
        if (res?.data?.data?.updatedAt) {
          setLastSavedAt(res.data.data.updatedAt);
        }
        Success("Reset Done", "Landing content restored to defaults on the server.");
      } catch (e) {
        console.error("Landing content reset failed:", e);
        Error(e?.response?.data?.message || "Failed to reset landing content.");
      } finally {
        setIsSaving(false);
      }
    });
  };

  // Video Actions
  const handleSaveVideo = async (e) => {
    e.preventDefault();
    const list = [...(content?.videoSection?.videos || [])];
    const url = videoModal.url.trim();
    if (!url) {
      Error("Video MP4 URL is required");
      return;
    }

    let poster = (videoModal.poster || "").trim();
    // Always build thumbnail from the video itself
    if (!poster || poster.startsWith("./images/") || poster.includes("/onboarding-app/images/")) {
      poster = await autoGenerateThumbnail(url);
    }
    if (!poster) {
      Error("Could not generate thumbnail from this video. Check the MP4 URL and try again.");
      return;
    }

    const item = {
      id: videoModal.index !== null && list[videoModal.index] ? list[videoModal.index].id : "vid-" + Date.now(),
      title: videoModal.title.trim(),
      description: videoModal.description.trim(),
      url,
      poster,
      tag: videoModal.tag.trim() || "Showcase"
    };
    if (videoModal.index !== null && videoModal.index < list.length) {
      list[videoModal.index] = item;
    } else {
      list.push(item);
    }
    const next = { ...content, videoSection: { ...(content.videoSection || {}), videos: list } };
    setVideoModal({ show: false, index: null, title: "", description: "", url: "", poster: "", tag: "" });
    persistToApi(next, "Success", videoModal.index !== null ? "Video updated on server!" : "New video saved on server!");
  };

  const handleDeleteVideo = (idx) => {
    Confirmation("Delete this video from the showcase?", "Delete", () => {
      setContent((prev) => {
        const list = (prev?.videoSection?.videos || []).filter((_, i) => i !== idx);
        const next = deepMergeContent({
          ...prev,
          videoSection: { ...(prev.videoSection || {}), videos: list }
        });
        setTimeout(() => {
          persistToApi(next, "Deleted", "Video removed from showcase on the server.");
        }, 0);
        return next;
      });
    });
  };

  // Steps Actions
  const handleSaveStep = (e) => {
    e.preventDefault();
    const list = [...(content?.howItWorks?.steps || [])];
    const item = {
      id: stepModal.index !== null && list[stepModal.index] ? list[stepModal.index].id : "step-" + Date.now(),
      title: stepModal.title.trim(),
      text: stepModal.text.trim()
    };
    if (stepModal.index !== null && stepModal.index < list.length) {
      list[stepModal.index] = item;
    } else {
      list.push(item);
    }
    setStepModal({ ...stepModal, show: false });
    persistToApi(
      { ...content, howItWorks: { ...(content.howItWorks || {}), steps: list } },
      "Success",
      "Step saved on the server!"
    );
  };

  const handleDeleteStep = (idx) => {
    Confirmation("Delete this step?", "Delete", () => {
      const list = (content?.howItWorks?.steps || []).filter((_, i) => i !== idx);
      persistToApi(
        { ...content, howItWorks: { ...(content.howItWorks || {}), steps: list } },
        "Deleted",
        "Step removed on the server."
      );
    });
  };

  // FAQ Actions
  const handleSaveFaq = (e) => {
    e.preventDefault();
    const list = [...(content?.faq?.items || [])];
    const item = {
      id: faqModal.index !== null && list[faqModal.index] ? list[faqModal.index].id : "faq-" + Date.now(),
      question: faqModal.question.trim(),
      answer: faqModal.answer.trim()
    };
    if (faqModal.index !== null && faqModal.index < list.length) {
      list[faqModal.index] = item;
    } else {
      list.push(item);
    }
    setFaqModal({ ...faqModal, show: false });
    persistToApi(
      { ...content, faq: { ...(content.faq || {}), items: list } },
      "Success",
      "FAQ saved on the server!"
    );
  };

  const handleDeleteFaq = (idx) => {
    Confirmation("Delete this FAQ item?", "Delete", () => {
      const list = (content?.faq?.items || []).filter((_, i) => i !== idx);
      persistToApi(
        { ...content, faq: { ...(content.faq || {}), items: list } },
        "Deleted",
        "FAQ removed on the server."
      );
    });
  };

  // Testimonials Actions
  const handleSaveTestimonial = (e) => {
    e.preventDefault();
    const list = [...(content?.testimonials?.items || [])];
    const item = {
      id: testimonialModal.index !== null && list[testimonialModal.index] ? list[testimonialModal.index].id : "test-" + Date.now(),
      name: testimonialModal.name.trim(),
      quote: testimonialModal.quote.trim(),
      image: testimonialModal.image.trim() || "./images/testimonials-client.png"
    };
    if (testimonialModal.index !== null && testimonialModal.index < list.length) {
      list[testimonialModal.index] = item;
    } else {
      list.push(item);
    }
    setTestimonialModal({ ...testimonialModal, show: false });
    persistToApi(
      { ...content, testimonials: { ...(content.testimonials || {}), items: list } },
      "Success",
      "Client review saved on the server!"
    );
  };

  const handleDeleteTestimonial = (idx) => {
    Confirmation("Delete this testimonial?", "Delete", () => {
      const list = (content?.testimonials?.items || []).filter((_, i) => i !== idx);
      persistToApi(
        { ...content, testimonials: { ...(content.testimonials || {}), items: list } },
        "Deleted",
        "Review removed on the server."
      );
    });
  };

  const tabs = [
    { id: "hero", label: "Hero Banner", icon: "fa-bolt" },
    { id: "videos", label: "Video Showcase", icon: "fa-video", count: (content?.videoSection?.videos || []).length },
    { id: "howItWorks", label: "How It Works", icon: "fa-list-ol", count: (content?.howItWorks?.steps || []).length },
    { id: "about", label: "About MUVRRS", icon: "fa-info-circle" },
    { id: "whyChooseUs", label: "Why Choose Us", icon: "fa-award" },
    { id: "testimonials", label: "Client Reviews", icon: "fa-comments", count: (content?.testimonials?.items || []).length },
    { id: "faq", label: "FAQ Accordion", icon: "fa-question-circle", count: (content?.faq?.items || []).length },
    { id: "ctaDownload", label: "Download CTA", icon: "fa-mobile-alt" },
    { id: "contact", label: "Contact & Socials", icon: "fa-address-book" },
  ];

  return (
    <div className="app-content content landing-cms-container">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="row">
              <div className="col-12">
            <div className="user-details-wrapper">
              <PageHeader
                title="Landing Page CMS"
                subtitle={
                  lastSavedAt
                    ? `Manage landing page videos, copy, and sections. Last saved ${new Date(lastSavedAt).toLocaleString()}`
                    : "Manage landing page videos, copy, and sections synced to the live page"
                }
                action={
                  <div className="d-flex align-items-center gap-2 flex-wrap landing-cms-header-actions">
                    <span className={`status-pill ${isSaving ? "status-pending" : "status-active"}`}>
                      {isLoading ? "Loading" : isSaving ? "Saving" : "Synced"}
                    </span>
                    <Link
                      to="/"
                      target="_blank"
                      className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
                    >
                      <i className="fas fa-external-link-alt me-2" />
                      Open Live Page
                    </Link>
                    <button
                      type="button"
                      className="siteBtn header-btn-standard w-100-mobile"
                      onClick={handleManualSave}
                      disabled={isLoading || isSaving}
                    >
                      <i className={`fas ${isSaving ? "fa-spinner fa-spin" : "fa-check"} me-2`} />
                      {isSaving ? "Saving…" : "Save All Changes"}
                    </button>
                  </div>
                }
              />

              {isLoading ? (
                <div className="cms-panel-card text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x text-warning mb-3" />
                  <p className="mb-0 text-muted">Loading landing content from server…</p>
                </div>
              ) : null}

              {/* Segmented Section Navigation Tabs */}
              {!isLoading && (
              <>
              <div className="cms-nav-tabs-wrapper">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`cms-nav-tab-btn ${activeTab === tab.id ? "is-active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={`fas ${tab.icon}`} />
                    <span>{tab.label}</span>
                    {tab.count !== undefined && (
                      <span className="cms-tab-badge">{tab.count}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* TAB 1: VIDEO SHOWCASE */}
              {activeTab === "videos" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-video" /></div>
                      <div>
                        <h4 className="cms-panel-title">Multi-Video Showcase ({(content?.videoSection?.videos || []).length} Videos)</h4>
                        <p className="cms-panel-desc">Manage playlist videos, direct MP4 streams, titles, and custom descriptions</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="cms-primary-btn"
                      onClick={() => setVideoModal({ show: true, index: null, title: "", description: "", url: "", poster: "", tag: "" })}
                    >
                      <i className="fas fa-plus" /> Add New Video
                    </button>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3">
                      {(content?.videoSection?.videos || []).map((item, index) => (
                        <div key={item.id || index} className="col-12 col-md-6 col-xl-4">
                          <div className="cms-item-card">
                            <div className="cms-item-thumb cms-video-thumb">
                              {item.url ? (
                                <video
                                  className="cms-video-preview"
                                  src={item.url}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  poster={resolveLandingMediaUrl(item.poster)}
                                />
                              ) : (
                                <img
                                  src={resolveLandingMediaUrl(item.poster)}
                                  alt={item.title}
                                  className="cms-video-poster"
                                />
                              )}
                              <span className="badge bg-warning text-dark position-absolute top-0 start-0 m-2 fw-bold">
                                {item.tag || `Video ${index + 1}`}
                              </span>
                              <span className="badge bg-dark text-white position-absolute bottom-0 end-0 m-2">
                                #{index + 1}
                              </span>
                            </div>

                            <h5 className="cms-item-title text-truncate" title={item.title}>
                              {item.title}
                            </h5>
                            <p className="cms-item-text" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {item.description}
                            </p>

                            <div className="d-flex align-items-center justify-content-between pt-2 border-top mt-auto">
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small text-primary text-decoration-none fw-bold d-inline-flex align-items-center gap-1"
                              >
                                <i className="fas fa-play-circle" /> Test MP4
                              </a>

                              <div className="btn-group btn-group-sm">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => setVideoModal({ show: true, index, title: item.title, description: item.description, url: item.url, poster: item.poster, tag: item.tag })}
                                >
                                  <i className="fas fa-edit" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => handleDeleteVideo(index)}
                                >
                                  <i className="fas fa-trash" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: HERO BANNER */}
              {activeTab === "hero" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-bolt" /></div>
                      <div>
                        <h4 className="cms-panel-title">Hero Banner Section</h4>
                        <p className="cms-panel-desc">Main headline, badge announcement, subtitle, and download links</p>
                      </div>
                    </div>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Hero Badge Announcement</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.hero?.badge || ""}
                          onChange={(e) => setContent({ ...content, hero: { ...(content.hero || {}), badge: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Hero Main Title</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.hero?.title || ""}
                          onChange={(e) => setContent({ ...content, hero: { ...(content.hero || {}), title: e.target.value } })}
                        />
                      </div>
                      <div className="col-12">
                        <label className="cms-form-label">Hero Subtitle</label>
                        <textarea
                          className="cms-form-control"
                          rows="3"
                          value={content.hero?.subtitle || ""}
                          onChange={(e) => setContent({ ...content, hero: { ...(content.hero || {}), subtitle: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">App Store Link</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.hero?.appStoreUrl || ""}
                          onChange={(e) => setContent({ ...content, hero: { ...(content.hero || {}), appStoreUrl: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Google Play Store Link</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.hero?.playStoreUrl || ""}
                          onChange={(e) => setContent({ ...content, hero: { ...(content.hero || {}), playStoreUrl: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: HOW IT WORKS */}
              {activeTab === "howItWorks" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-list-ol" /></div>
                      <div>
                        <h4 className="cms-panel-title">How It Works Steps ({(content?.howItWorks?.steps || []).length} Steps)</h4>
                        <p className="cms-panel-desc">Manage onboarding steps and procedure instructions</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="cms-primary-btn"
                      onClick={() => setStepModal({ show: true, index: null, title: "Step " + ((content?.howItWorks?.steps || []).length + 1), text: "" })}
                    >
                      <i className="fas fa-plus" /> Add Step
                    </button>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3 mb-4">
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Section Eyebrow</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.howItWorks?.eyebrow || ""}
                          onChange={(e) => setContent({ ...content, howItWorks: { ...(content.howItWorks || {}), eyebrow: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Section Heading</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.howItWorks?.title || ""}
                          onChange={(e) => setContent({ ...content, howItWorks: { ...(content.howItWorks || {}), title: e.target.value } })}
                        />
                      </div>
                      <div className="col-12">
                        <label className="cms-form-label">Section Subtitle</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.howItWorks?.subtitle || ""}
                          onChange={(e) => setContent({ ...content, howItWorks: { ...(content.howItWorks || {}), subtitle: e.target.value } })}
                        />
                      </div>
                    </div>

                    <h6 className="fw-bold mb-3 text-dark">Configured Steps:</h6>
                    <div className="row g-3">
                      {(content?.howItWorks?.steps || []).map((s, idx) => (
                        <div key={s.id || idx} className="col-12 col-md-4">
                          <div className="cms-item-card">
                            <span className="cms-item-tag">{s.title}</span>
                            <p className="cms-item-text">{s.text}</p>
                            <div className="d-flex justify-content-end gap-2 pt-2 border-top mt-auto">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setStepModal({ show: true, index: idx, title: s.title, text: s.text })}
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteStep(idx)}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 4: ABOUT MUVRRS */}
              {activeTab === "about" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-info-circle" /></div>
                      <div>
                        <h4 className="cms-panel-title">About MUVRRS Section</h4>
                        <p className="cms-panel-desc">Brand overview, mission statement, and key value pillars</p>
                      </div>
                    </div>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3 mb-4">
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Section Title</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.about?.title || ""}
                          onChange={(e) => setContent({ ...content, about: { ...(content.about || {}), title: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Eyebrow</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.about?.eyebrow || ""}
                          onChange={(e) => setContent({ ...content, about: { ...(content.about || {}), eyebrow: e.target.value } })}
                        />
                      </div>
                      <div className="col-12">
                        <label className="cms-form-label">About Description</label>
                        <textarea
                          className="cms-form-control"
                          rows="3"
                          value={content.about?.subtitle || ""}
                          onChange={(e) => setContent({ ...content, about: { ...(content.about || {}), subtitle: e.target.value } })}
                        />
                      </div>
                    </div>

                    <h6 className="fw-bold mb-3 text-dark">Key Value Pillars:</h6>
                    <div className="row g-3">
                      {(content.about?.features || []).map((f, idx) => (
                        <div key={f.id || idx} className="col-12 col-md-4">
                          <div className="p-3 border rounded-3 bg-light">
                            <label className="cms-form-label">Pillar {idx + 1} Title</label>
                            <input
                              type="text"
                              className="cms-form-control mb-2"
                              value={f.title}
                              onChange={(e) => {
                                const list = [...(content.about?.features || [])];
                                list[idx] = { ...list[idx], title: e.target.value };
                                setContent({ ...content, about: { ...(content.about || {}), features: list } });
                              }}
                            />
                            <label className="cms-form-label">Description</label>
                            <textarea
                              className="cms-form-control"
                              rows="2"
                              value={f.text}
                              onChange={(e) => {
                                const list = [...(content.about?.features || [])];
                                list[idx] = { ...list[idx], text: e.target.value };
                                setContent({ ...content, about: { ...(content.about || {}), features: list } });
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 5: WHY CHOOSE US */}
              {activeTab === "whyChooseUs" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-award" /></div>
                      <div>
                        <h4 className="cms-panel-title">Why Choose Us (Bento Grid)</h4>
                        <p className="cms-panel-desc">Featured banner card, verified statistics, and 4 benefit cards</p>
                      </div>
                    </div>
                  </div>

                  <div className="cms-panel-body">
                    <div className="p-4 border rounded-3 mb-4 bg-light">
                      <h6 className="fw-bold text-primary mb-3">★ Featured Card & Stats Counters:</h6>
                      <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6">
                          <label className="cms-form-label">Featured Card Title</label>
                          <input
                            type="text"
                            className="cms-form-control"
                            value={content.whyChooseUs?.featured?.title || ""}
                            onChange={(e) => setContent({ ...content, whyChooseUs: { ...(content.whyChooseUs || {}), featured: { ...(content.whyChooseUs?.featured || {}), title: e.target.value } } })}
                          />
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="cms-form-label">Featured Description</label>
                          <input
                            type="text"
                            className="cms-form-control"
                            value={content.whyChooseUs?.featured?.text || ""}
                            onChange={(e) => setContent({ ...content, whyChooseUs: { ...(content.whyChooseUs || {}), featured: { ...(content.whyChooseUs?.featured || {}), text: e.target.value } } })}
                          />
                        </div>
                      </div>

                      <div className="row g-2">
                        {(content.whyChooseUs?.featured?.stats || []).map((st, i) => (
                          <div key={i} className="col-12 col-sm-4">
                            <input
                              type="text"
                              className="cms-form-control fw-bold text-center mb-1"
                              value={st.value}
                              onChange={(e) => {
                                const stats = [...(content.whyChooseUs?.featured?.stats || [])];
                                stats[i] = { ...stats[i], value: e.target.value };
                                setContent({ ...content, whyChooseUs: { ...(content.whyChooseUs || {}), featured: { ...(content.whyChooseUs?.featured || {}), stats } } });
                              }}
                            />
                            <input
                              type="text"
                              className="cms-form-control small text-center"
                              value={st.label}
                              onChange={(e) => {
                                const stats = [...(content.whyChooseUs?.featured?.stats || [])];
                                stats[i] = { ...stats[i], label: e.target.value };
                                setContent({ ...content, whyChooseUs: { ...(content.whyChooseUs || {}), featured: { ...(content.whyChooseUs?.featured || {}), stats } } });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <h6 className="fw-bold mb-3 text-dark">4 Bento Benefit Cards:</h6>
                    <div className="row g-3">
                      {(content.whyChooseUs?.cards || []).map((c, i) => (
                        <div key={c.id || i} className="col-12 col-md-6">
                          <div className="p-3 border rounded-3">
                            <label className="cms-form-label">Card {i + 1} Title</label>
                            <input
                              type="text"
                              className="cms-form-control mb-2"
                              value={c.title}
                              onChange={(e) => {
                                const list = [...(content.whyChooseUs?.cards || [])];
                                list[i] = { ...list[i], title: e.target.value };
                                setContent({ ...content, whyChooseUs: { ...(content.whyChooseUs || {}), cards: list } });
                              }}
                            />
                            <label className="cms-form-label">Description</label>
                            <textarea
                              className="cms-form-control"
                              rows="2"
                              value={c.text}
                              onChange={(e) => {
                                const list = [...(content.whyChooseUs?.cards || [])];
                                list[i] = { ...list[i], text: e.target.value };
                                setContent({ ...content, whyChooseUs: { ...(content.whyChooseUs || {}), cards: list } });
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 6: CLIENT REVIEWS / TESTIMONIALS */}
              {activeTab === "testimonials" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-comments" /></div>
                      <div>
                        <h4 className="cms-panel-title">Client Reviews & Testimonials ({(content.testimonials?.items || []).length})</h4>
                        <p className="cms-panel-desc">Real customer feedback, client quotes, and profile avatars</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="cms-primary-btn"
                      onClick={() => setTestimonialModal({ show: true, index: null, name: "", quote: "", image: "./images/testimonials-client.png" })}
                    >
                      <i className="fas fa-plus" /> Add Review
                    </button>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3">
                      {(content.testimonials?.items || []).map((t, idx) => (
                        <div key={t.id || idx} className="col-12 col-md-4">
                          <div className="cms-item-card">
                            <div className="d-flex align-items-center gap-3 mb-3">
                              <img
                                src={t.image || "./images/testimonials-client.png"}
                                alt={t.name}
                                style={{ width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover" }}
                              />
                              <div>
                                <h6 className="fw-bold mb-0 text-dark">{t.name}</h6>
                                <small className="text-warning">★★★★★</small>
                              </div>
                            </div>
                            <p className="cms-item-text fst-italic">"{t.quote}"</p>
                            <div className="d-flex justify-content-end gap-2 pt-2 border-top mt-auto">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setTestimonialModal({ show: true, index: idx, name: t.name, quote: t.quote, image: t.image })}
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteTestimonial(idx)}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 7: FAQ ACCORDION */}
              {activeTab === "faq" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-question-circle" /></div>
                      <div>
                        <h4 className="cms-panel-title">Frequently Asked Questions ({(content.faq?.items || []).length})</h4>
                        <p className="cms-panel-desc">Manage accordion questions and comprehensive customer answers</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="cms-primary-btn"
                      onClick={() => setFaqModal({ show: true, index: null, question: "", answer: "" })}
                    >
                      <i className="fas fa-plus" /> Add FAQ
                    </button>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3">
                      {(content.faq?.items || []).map((item, idx) => (
                        <div key={item.id || idx} className="col-12">
                          <div className="p-3 border rounded-3 bg-white d-flex align-items-start justify-content-between gap-3">
                            <div className="flex-grow-1">
                              <h6 className="fw-bold text-dark mb-1">
                                <i className="fas fa-question text-warning me-2" />
                                {item.question}
                              </h6>
                              <p className="text-muted small mb-0 ps-4">{item.answer}</p>
                            </div>
                            <div className="btn-group btn-group-sm">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => setFaqModal({ show: true, index: idx, question: item.question, answer: item.answer })}
                              >
                                <i className="fas fa-edit" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteFaq(idx)}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 8: DOWNLOAD CTA */}
              {activeTab === "ctaDownload" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-mobile-alt" /></div>
                      <div>
                        <h4 className="cms-panel-title">Download Banner CTA</h4>
                        <p className="cms-panel-desc">Orange banner with app download links</p>
                      </div>
                    </div>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="cms-form-label">Banner Title</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.ctaDownload?.title || ""}
                          onChange={(e) => setContent({ ...content, ctaDownload: { ...(content.ctaDownload || {}), title: e.target.value } })}
                        />
                      </div>
                      <div className="col-12">
                        <label className="cms-form-label">Banner Subtitle</label>
                        <textarea
                          className="cms-form-control"
                          rows="2"
                          value={content.ctaDownload?.subtitle || ""}
                          onChange={(e) => setContent({ ...content, ctaDownload: { ...(content.ctaDownload || {}), subtitle: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">App Store Download URL</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.ctaDownload?.appStoreUrl || ""}
                          onChange={(e) => setContent({ ...content, ctaDownload: { ...(content.ctaDownload || {}), appStoreUrl: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="cms-form-label">Google Play Store Download URL</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.ctaDownload?.playStoreUrl || ""}
                          onChange={(e) => setContent({ ...content, ctaDownload: { ...(content.ctaDownload || {}), playStoreUrl: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 9: CONTACT & SOCIALS */}
              {activeTab === "contact" && (
                <div className="cms-panel-card">
                  <div className="cms-panel-header">
                    <div className="cms-panel-header-left">
                      <div className="cms-panel-icon"><i className="fas fa-address-book" /></div>
                      <div>
                        <h4 className="cms-panel-title">Contact & Social Media Settings</h4>
                        <p className="cms-panel-desc">Physical address, support phone, email, and social networks</p>
                      </div>
                    </div>
                  </div>

                  <div className="cms-panel-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <label className="cms-form-label">Support Phone</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.contact?.phone || ""}
                          onChange={(e) => setContent({ ...content, contact: { ...(content.contact || {}), phone: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="cms-form-label">Support Email</label>
                        <input
                          type="email"
                          className="cms-form-control"
                          value={content.contact?.email || ""}
                          onChange={(e) => setContent({ ...content, contact: { ...(content.contact || {}), email: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="cms-form-label">Headquarters Address</label>
                        <input
                          type="text"
                          className="cms-form-control"
                          value={content.contact?.address || ""}
                          onChange={(e) => setContent({ ...content, contact: { ...(content.contact || {}), address: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-lg-3">
                        <label className="cms-form-label">Facebook URL</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.socials?.facebook || ""}
                          onChange={(e) => setContent({ ...content, socials: { ...(content.socials || {}), facebook: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-lg-3">
                        <label className="cms-form-label">Twitter / X URL</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.socials?.twitter || ""}
                          onChange={(e) => setContent({ ...content, socials: { ...(content.socials || {}), twitter: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-lg-3">
                        <label className="cms-form-label">Instagram URL</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.socials?.instagram || ""}
                          onChange={(e) => setContent({ ...content, socials: { ...(content.socials || {}), instagram: e.target.value } })}
                        />
                      </div>
                      <div className="col-12 col-sm-6 col-lg-3">
                        <label className="cms-form-label">LinkedIn URL</label>
                        <input
                          type="url"
                          className="cms-form-control font-monospace small"
                          value={content.socials?.linkedin || ""}
                          onChange={(e) => setContent({ ...content, socials: { ...(content.socials || {}), linkedin: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sticky Save Bar */}
              <div className="cms-save-bar">
                <div className="cms-save-bar-info d-flex align-items-center gap-3">
                  <div className="cms-save-bar-icon">
                    <i className="fas fa-cloud-upload-alt" />
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold">Content Sync</h6>
                    <small className="text-muted">
                      {isSaving
                        ? "Saving to server…"
                        : "Save to publish changes to the live landing page"}
                    </small>
                  </div>
                </div>

                <div className="cms-save-bar-actions d-flex align-items-center gap-2 flex-wrap">
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                  >
                    <i className="fas fa-external-link-alt" /> Preview Page
                  </a>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleResetDefaults}
                    disabled={isLoading || isSaving}
                  >
                    <i className="fas fa-undo me-1" /> Reset Defaults
                  </button>
                  <button
                    type="button"
                    className="siteBtn"
                    onClick={handleManualSave}
                    disabled={isLoading || isSaving}
                  >
                    <i className={`fas ${isSaving ? "fa-spinner fa-spin" : "fa-save"} me-2`} />
                    {isSaving ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
              </>
              )}

            </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* VIDEO MODAL */}
      <Modal show={videoModal.show} onHide={() => setVideoModal({ ...videoModal, show: false })} centered size="lg">
        <form onSubmit={handleSaveVideo}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fas fa-video text-warning me-2" />
              {videoModal.index !== null ? "Edit Video Details" : "Add New Showcase Video"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="row g-3">
              <div className="col-12 col-md-8">
                <label className="cms-form-label">Video Title *</label>
                <input
                  type="text"
                  className="cms-form-control"
                  value={videoModal.title}
                  onChange={(e) => setVideoModal({ ...videoModal, title: e.target.value })}
                  placeholder="e.g. How to Book Same-Day Delivery"
                  required
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="cms-form-label">Category / Tag</label>
                <input
                  type="text"
                  className="cms-form-control"
                  value={videoModal.tag}
                  onChange={(e) => setVideoModal({ ...videoModal, tag: e.target.value })}
                  placeholder="e.g. Overview, Safety, How It Works"
                />
              </div>
              <div className="col-12">
                <label className="cms-form-label">Video MP4 URL *</label>
                <input
                  type="url"
                  className="cms-form-control font-monospace small"
                  value={videoModal.url}
                  onChange={(e) => setVideoModal({ ...videoModal, url: e.target.value, poster: "" })}
                  placeholder="https://.../video.mp4"
                  required
                />
              </div>
              <div className="col-12">
                <label className="cms-form-label">Video Description</label>
                <textarea
                  className="cms-form-control"
                  rows="3"
                  value={videoModal.description}
                  onChange={(e) => setVideoModal({ ...videoModal, description: e.target.value })}
                  placeholder="Short description of this video..."
                />
              </div>
              <div className="col-12">
                <label className="cms-form-label">
                  Auto Thumbnail
                  {thumbnailGenerating ? (
                    <span className="ms-2 text-warning small">
                      <i className="fas fa-spinner fa-spin me-1" />
                      Generating from video…
                    </span>
                  ) : videoModal.poster ? (
                    <span className="ms-2 text-success small">
                      <i className="fas fa-check-circle me-1" />
                      Ready
                    </span>
                  ) : null}
                </label>
                <div className="cms-auto-thumb-wrap">
                  {thumbnailGenerating ? (
                    <div className="cms-video-modal-preview cms-thumb-loading">
                      <i className="fas fa-spinner fa-spin fa-2x text-warning" />
                      <span>Capturing frame from MP4…</span>
                    </div>
                  ) : videoModal.poster ? (
                    <img
                      className="cms-video-modal-preview"
                      src={resolveLandingMediaUrl(videoModal.poster)}
                      alt="Auto-generated thumbnail"
                    />
                  ) : (
                    <div className="cms-video-modal-preview cms-thumb-loading">
                      <i className="fas fa-image fa-2x text-muted mb-2" />
                      <span>Paste an MP4 URL — thumbnail generates automatically</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setVideoModal({ show: false, index: null, title: "", description: "", url: "", poster: "", tag: "" })}>
              Cancel
            </button>
            <button type="submit" className="cms-primary-btn" disabled={thumbnailGenerating || isSaving}>
              <i className={`fas ${thumbnailGenerating ? "fa-spinner fa-spin" : "fa-check"}`} />
              {thumbnailGenerating ? "Generating…" : "Save Video"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* STEP MODAL */}
      <Modal show={stepModal.show} onHide={() => setStepModal({ ...stepModal, show: false })} centered>
        <form onSubmit={handleSaveStep}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fas fa-list-ol text-warning me-2" />
              {stepModal.index !== null ? "Edit Step" : "Add New Step"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="mb-3">
              <label className="cms-form-label">Step Label / Title *</label>
              <input
                type="text"
                className="cms-form-control"
                value={stepModal.title}
                onChange={(e) => setStepModal({ ...stepModal, title: e.target.value })}
                required
              />
            </div>
            <div className="mb-3">
              <label className="cms-form-label">Instruction Text *</label>
              <textarea
                className="cms-form-control"
                rows="3"
                value={stepModal.text}
                onChange={(e) => setStepModal({ ...stepModal, text: e.target.value })}
                required
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setStepModal({ ...stepModal, show: false })}>
              Cancel
            </button>
            <button type="submit" className="cms-primary-btn">Save Step</button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* FAQ MODAL */}
      <Modal show={faqModal.show} onHide={() => setFaqModal({ ...faqModal, show: false })} centered size="lg">
        <form onSubmit={handleSaveFaq}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fas fa-question-circle text-warning me-2" />
              {faqModal.index !== null ? "Edit FAQ" : "Add New FAQ"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="mb-3">
              <label className="cms-form-label">Question *</label>
              <input
                type="text"
                className="cms-form-control"
                value={faqModal.question}
                onChange={(e) => setFaqModal({ ...faqModal, question: e.target.value })}
                required
              />
            </div>
            <div className="mb-3">
              <label className="cms-form-label">Answer *</label>
              <textarea
                className="cms-form-control"
                rows="4"
                value={faqModal.answer}
                onChange={(e) => setFaqModal({ ...faqModal, answer: e.target.value })}
                required
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setFaqModal({ ...faqModal, show: false })}>
              Cancel
            </button>
            <button type="submit" className="cms-primary-btn">Save FAQ</button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* TESTIMONIAL MODAL */}
      <Modal show={testimonialModal.show} onHide={() => setTestimonialModal({ ...testimonialModal, show: false })} centered size="lg">
        <form onSubmit={handleSaveTestimonial}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fas fa-comments text-warning me-2" />
              {testimonialModal.index !== null ? "Edit Review" : "Add Client Review"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="cms-form-label">Client Name *</label>
                <input
                  type="text"
                  className="cms-form-control"
                  value={testimonialModal.name}
                  onChange={(e) => setTestimonialModal({ ...testimonialModal, name: e.target.value })}
                  required
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="cms-form-label">Avatar Image URL</label>
                <input
                  type="text"
                  className="cms-form-control"
                  value={testimonialModal.image}
                  onChange={(e) => setTestimonialModal({ ...testimonialModal, image: e.target.value })}
                />
              </div>
              <div className="col-12">
                <label className="cms-form-label">Review Quote *</label>
                <textarea
                  className="cms-form-control"
                  rows="3"
                  value={testimonialModal.quote}
                  onChange={(e) => setTestimonialModal({ ...testimonialModal, quote: e.target.value })}
                  required
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => setTestimonialModal({ ...testimonialModal, show: false })}>
              Cancel
            </button>
            <button type="submit" className="cms-primary-btn">Save Review</button>
          </Modal.Footer>
        </form>
      </Modal>

    </div>
  );
}
